// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { stopSubmit, setSubmitFailed, touch } from 'redux-form';

import {
    getAgreementTypeOptionList,
    getInsuranceTypeOptionList,
    getPropertyTypeOptionList,
    getStationOptionList,
    getPropertyStatusOptionList,
    getPropertyPromotionTypesOptionList,
    getContacts,
    getDefaultTextsByFieldName,
} from '../../selectors/shared';

import {
    loadProperty,
    updateProperty,
    getValidationErrorsForForm,
    loadAgreementTypes,
    loadInsuranceTypes,
} from '../../redux/reducers/property';
import { loadDefaultTexts } from '../../redux/reducers/defaulttext';
import { loadStations } from '../../redux/reducers/stations';
import { loadContacts } from '../../redux/reducers/contacts';

import { loadContactRequestRegions } from '../../redux/reducers/contactrequests';

import { getFormValue, getFormAssetId } from '../../shared/utils';

import Loader from '../../components/Loader/Loader';
import MessageBlock from '../../components/MessageBlock/MessageBlock';

import FormWrapper from '../../components/shared/FormWrapper';
import PropertyNav from '../../components/shared/PropertyNav';

// different forms dependant on section
import PropertyLocationForm, {
    validate as validateLocation,
} from '../../components/PropertyForms/PropertyLocationForm';
import PropertyDescriptionForm, {
    validate as validateDescription,
} from '../../components/PropertyForms/PropertyDescriptionForm';
import PropertyKeyInformationForm, {
    validate as validateKeyInformation,
} from '../../components/PropertyForms/PropertyKeyInformationForm';
import PropertyCostsForm, {
    validate as validateCosts,
} from '../../components/PropertyForms/PropertyCostsForm';
import PropertyContactForm, {
    validate as validateContacts,
} from '../../components/PropertyForms/PropertyContactForm';
import PropertyMediaForm, {
    validate as validateMedia,
} from '../../components/PropertyForms/PropertyMediaForm';
import PropertyActivityLogForm from '../../components/PropertyForms/PropertyActivityLogForm';

const FORM_SECTIONS = {
    location: {
        formName: 'propertyEditLocation',
    },
    description: {
        formName: 'propertyEditDescription',
    },
    'key-information': {
        formName: 'propertyEditKeyInformation',
    },
    costs: {
        formName: 'propertyEditCosts',
    },
    contacts: {
        formName: 'propertyEditContacts',
    },
    media: {
        formName: 'propertyEditMedia',
    },
    'activity-log': {
        formName: 'propertyActivityLogs',
    },
};

class PropertyEdit extends Component {
    onSubmit: Function;
    getSectionStatuses: Function;

    constructor(props) {
        super(props);

        this.getSectionStatuses = this.getSectionStatuses.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    componentDidMount() {
        this.props.dispatch(
            loadProperty(this.props.match.params.propertyReference)
        );

        this.props.dispatch(loadStations(1, 9999));
        this.props.dispatch(loadContacts(1, 9999));
        this.props.dispatch(loadDefaultTexts());
        this.props.dispatch(loadAgreementTypes());
        this.props.dispatch(loadInsuranceTypes());
        this.props.dispatch(loadContactRequestRegions());
    }

    getSectionStatuses() {
        const { property } = this.props;

        const locationValues = {
            reference: property.propertyReference,
            name: property.propertyName,
            address: property.propertyAddress,
            townCity: property.propertyTown,
            postcode: property.propertyPostCode,
            regionId: property.propertyRegionId,
            countyId: property.propertyCountyId,
            boroughId: property.propertyBoroughId,
            businessEstateId: property.propertyBusinessEstateId,
            nearestStationId: property.propertyStationId,
            geolocation:
                property.propertyLat && property.propertyLong
                    ? {
                          lat: property.propertyLat,
                          lng: property.propertyLong,
                      }
                    : null,
        };

        const descriptionValues = {
            availableFrom: property.propertyAvailableFrom,
            status: property.propertyStatus,
            propertyTypeIds: property.propertyTypes.map(
                type => type.propertyTypeId
            ),
            description: property.propertyDescription,
            locationDescription: property.propertyLocationDescription,
            userPlanning: property.propertyUserPlanning,
            otherInformation: property.propertyOtherInformation,
            serviceCharge: property.propertyServiceCharge,
            maintenance: property.propertyMaintenance,
        };

        const keyInformationValues = {
            keyDetails1: property.propertyKeyDetails1,
            keyDetails2: property.propertyKeyDetails2,
            keyDetails3: property.propertyKeyDetails3,
            keyDetails4: property.propertyKeyDetails4,
            keyDetails5: property.propertyKeyDetails5,
            agreementTypeId: property.propertyPropertyAgreementTypeId,
            agreementTypeDescription:
                property.propertyPropertyAgreementDescription,
            solutionsRPI: property.propertySolutionsRPI,
            rent: property.propertyRent,
            rentText: property.propertyRentText,
            size: property.propertySize,
            rateableValue: property.propertyRateableValue,
            insuranceTypeId: property.propertyPropertyInsuranceId,
            insuranceAmount: property.propertyPropertyInsuranceAmount,
            repairGuarantee: property.propertyHasRepairGuarantee ? 'yes' : 'no',
        };

        const mediaValues = {
            brochureAsset: property.propertyBrochureAsset,
            mainPhotoAsset: property.propertyMainPhotoAsset,
            photos: Array.isArray(property.imageLookups)
                ? property.imageLookups.map(lookup => {
                      return {
                          asset: lookup.image,
                      };
                  })
                : [],
            videos: Array.isArray(property.videos)
                ? property.videos.map(video => {
                      return {
                          videoCode: video.propertyVideoCode,
                          videoTitle: video.propertyVideoTitle,
                      };
                  })
                : [],
            plasmaVideoCode: property.propertyPlasmaVideoCode,
            plasmaVideoTitle: property.propertyPlasmaVideoTitle,
        };

        const costsValues = {
            localAuthority: property.propertyLocalAuthority,
        };

        const contactValues = {
            contacts: Array.isArray(property.contacts) ? property.contacts : [],
            showContactMoreInfo: property.propertyContactMoreInfo
                ? 'yes'
                : 'no',
            contactRequestByRegion: property.propertyIsContactRequestByRegion
                ? 'yes'
                : 'no',
            contactRequestRegionId: property.propertyContactRequestRegionId,
        };

        const locationErrors = validateLocation(locationValues);
        const descriptionErrors = validateDescription(descriptionValues);
        const keyInformationErrors = validateKeyInformation(
            keyInformationValues
        );
        const mediaErrors = validateMedia(mediaValues);
        const costsErrors = validateCosts(costsValues);
        const contactErrors = validateContacts(contactValues);

        return {
            location: Object.keys(locationErrors).length > 0,
            description: Object.keys(descriptionErrors).length > 0,
            keyInformation: Object.keys(keyInformationErrors).length > 0,
            media: Object.keys(mediaErrors).length > 0,
            costs: Object.keys(costsErrors).length > 0,
            contacts: Object.keys(contactErrors).length > 0,
        };
    }

    onSubmit(formValues) {
        const {
            dispatch,
            history,
            match: { params: { section } },
            property,
        } = this.props;

        const currentSection = section ? section.toLowerCase() : 'location';
        const sectionKeys = Object.keys(FORM_SECTIONS);
        const currentFormName = FORM_SECTIONS[currentSection].formName;
        const nextSectionIndex = FORM_SECTIONS[currentSection]
            ? sectionKeys.indexOf(currentSection) + 1
            : 0;
        const isLastSection = nextSectionIndex > sectionKeys.length - 1;
        const nextSectionKey = sectionKeys[nextSectionIndex] || 'location';

        let propertyData = {};

        if (!section) {
            propertyData = {
                propertyReference: getFormValue(formValues, 'reference'),
                propertyName: getFormValue(formValues, 'name'),
                propertyAddress: formValues.address ? formValues.address : '',
                propertyTown: getFormValue(formValues, 'townCity'),
                propertyPostCode: getFormValue(formValues, 'postcode'),
                propertyBusinessEstateId: getFormValue(
                    formValues,
                    'businessEstateId'
                ),
                propertyStationId: getFormValue(formValues, 'nearestStationId'),
                propertyRegionId: getFormValue(formValues, 'regionId'),
                propertyCountyId: getFormValue(formValues, 'countyId'),
                propertyBoroughId: getFormValue(formValues, 'boroughId'),
                propertyLat: formValues.geolocation
                    ? formValues.geolocation.lat
                    : null,
                propertyLong: formValues.geolocation
                    ? formValues.geolocation.lng
                    : null,
            };
        }

        if (section === 'description') {
            propertyData = {
                propertyAvailableFrom: getFormValue(
                    formValues,
                    'availableFrom'
                ),
                propertyStatus: getFormValue(formValues, 'status'),
                propertyDescription: getFormValue(formValues, 'description'),
                propertyLocationDescription: getFormValue(
                    formValues,
                    'locationDescription'
                ),
                propertyUserPlanning: getFormValue(formValues, 'userPlanning'),
                propertyOtherInformation: getFormValue(
                    formValues,
                    'otherInformation'
                ),
                propertyServiceCharge: getFormValue(
                    formValues,
                    'serviceCharge'
                ),
                propertyMaintenance: getFormValue(formValues, 'maintenance'),
                propertyTypes: formValues.propertyTypeIds.map(
                    propertyTypeId => {
                        return { propertyTypeId };
                    }
                ),
            };
        }

        if (section === 'key-information') {
            propertyData = {
                propertyKeyDetails1: getFormValue(formValues, 'keyDetails1'),
                propertyKeyDetails2: getFormValue(formValues, 'keyDetails2'),
                propertyKeyDetails3: getFormValue(formValues, 'keyDetails3'),
                propertyKeyDetails4: getFormValue(formValues, 'keyDetails4'),
                propertyKeyDetails5: getFormValue(formValues, 'keyDetails5'),
                propertyPropertyAgreementTypeId: getFormValue(
                    formValues,
                    'agreementTypeId'
                ),
                propertyPropertyAgreementDescription: getFormValue(
                    formValues,
                    'agreementTypeDescription'
                ),
                propertySolutionsRPI: getFormValue(formValues, 'solutionsRPI'),
                propertyRent: parseInt(formValues.rent, 10),
                propertyRentText: getFormValue(formValues, 'rentText'),
                propertySize: parseInt(formValues.size, 10),
                propertyRateableValue: parseInt(formValues.rateableValue, 10),
                propertyPropertyInsuranceId: getFormValue(
                    formValues,
                    'insuranceTypeId'
                ),
                propertyPropertyInsuranceOther: getFormValue(
                    formValues,
                    'insuranceTypeOther'
                ),
                propertyPropertyInsuranceAmount: getFormValue(
                    formValues,
                    'insuranceAmount'
                ),
                propertyHasRepairGuarantee:
                    formValues.repairGuarantee === 'yes',
            };
        }

        if (section === 'costs') {
            propertyData = {
                propertyLocalAuthority: getFormValue(
                    formValues,
                    'localAuthority'
                ),
                propertyDeposit: getFormValue(formValues, 'deposit'),
                propertyCosts: getFormValue(formValues, 'costs'),
                propertyBusinessRates: getFormValue(
                    formValues,
                    'businessRates'
                ),
                propertyPromoType: getFormValue(formValues, 'promotionType'),
                propertyPromoAfterPrice: getFormValue(
                    formValues,
                    'promotionAfterPrice'
                ),
                propertyPromoRentFreeMonths: getFormValue(
                    formValues,
                    'rentFreeMonths'
                ),
            };
        }

        if (section === 'contacts') {
            propertyData = {
                propertyContacts: Array.isArray(formValues.contacts)
                    ? formValues.contacts.map(contact => {
                          return {
                              contactId: contact.contactId,
                          };
                      })
                    : [],
                propertyContactMoreInfo:
                    formValues.showContactMoreInfo === 'yes',
                propertyIsContactRequestByRegion:
                    formValues.contactRequestByRegion === 'yes',
                propertyContactRequestRegionId: getFormValue(
                    formValues,
                    'contactRequestRegionId'
                ),
            };
        }

        if (section === 'media') {
            propertyData = {
                propertyBrochureAssetId: getFormAssetId(
                    formValues,
                    'brochureAsset'
                ),
                propertyMainPhotoAssetId: getFormAssetId(
                    formValues,
                    'mainPhotoAsset'
                ),
                propertyImages: Array.isArray(formValues.photos)
                    ? formValues.photos
                          .filter(
                              photo =>
                                  photo.asset &&
                                  photo.asset.assetId &&
                                  photo.asset.assetId.length > 0
                          )
                          .map((photo, index) => {
                              return {
                                  propertyImageAssetId: photo.asset.assetId,
                                  propertyImageOrder: index + 1,
                              };
                          })
                    : null,
                propertyFiles: Array.isArray(formValues.additionalFiles)
                    ? formValues.additionalFiles
                          .filter(
                              file =>
                                  file.asset &&
                                  file.asset.assetId &&
                                  file.asset.assetId.length > 0
                          )
                          .map((file, index) => {
                              return {
                                  propertyFileAssetId: file.asset.assetId,
                                  propertyFileOrder: index + 1,
                              };
                          })
                    : null,
                propertyVideos: Array.isArray(formValues.videos)
                    ? formValues.videos.map((video, index) => {
                          return {
                              propertyVideoCode: video.videoCode,
                              propertyVideoTitle: video.videoTitle,
                              propertyVideoOrder: index + 1,
                          };
                      })
                    : null,
                propertyPlasmaVideoCode: getFormValue(
                    formValues,
                    'plasmaVideoCode'
                ),
                propertyPlasmaVideoTitle: getFormValue(
                    formValues,
                    'plasmaVideoTitle'
                ),
            };
        }

        return dispatch(
            updateProperty(property.propertyId, propertyData, currentFormName)
        )
            .then(updatedProperty => {
                const destination = isLastSection
                    ? '/property'
                    : `/property/edit/${updatedProperty.propertyReference}/${nextSectionKey}`;

                history.push(destination);
            })
            .catch(err => {
                // Error notification is shown by updateProperty — stay on the current section
                const formErrors = getValidationErrorsForForm(err);
                if (formErrors) {
                    dispatch(stopSubmit(currentFormName, formErrors));
                    dispatch(setSubmitFailed(currentFormName));
                    Object.keys(formErrors).forEach(field =>
                        dispatch(touch(currentFormName, field))
                    );
                }
                throw err;
            });
    }

    render() {
        const {
            boroughs,
            businessEstates,
            contactList,
            counties,
            errorFetching,
            isFetching,
            fetchingContacts,
            standardText,
            match: { params: { propertyReference, section } },
            property,
            regions,

            agreementTypeOptions,
            insuranceTypeOptions,
            stationOptions,
            propertyTypeOptions,
            propertyStatusOptions,
            propertyPromotionOptions,
            fetchingStations,
            contactRequestRegionOptions,
        } = this.props;

        const showStationLoader = section === undefined && fetchingStations;
        const sectionStatuses =
            !isFetching && property ? this.getSectionStatuses() : null;
        let activityLogList = {};

        if (errorFetching) {
            return (
                <FormWrapper>
                    <MessageBlock message="An unexpected error occurred whilst loading this property." />
                </FormWrapper>
            );
        }

        if (isFetching) {
            return (
                <FormWrapper>
                    <Loader message="Loading property, please wait..." />
                </FormWrapper>
            );
        }

        if (standardText.fetching) {
            return (
                <FormWrapper>
                    <Loader message="Loading standard texts, please wait..." />
                </FormWrapper>
            );
        }

        if (showStationLoader) {
            return (
                <FormWrapper>
                    <Loader message="Loading station data, please wait..." />
                </FormWrapper>
            );
        }

        if (!isFetching && property) {
            let initialValues = {};

            const reassignedContacts = property.contacts.reduce(
                (result, contact) => {
                    const assigns =
                        contact && contact.contactAssigns
                            ? contact.contactAssigns.filter(Boolean)
                            : [];
                    return result.concat(assigns);
                },
                []
            );

            // location section
            if (section === undefined) {
                initialValues = {
                    reference: property.propertyReference,
                    name: property.propertyName,
                    address: property.propertyAddress,
                    townCity: property.propertyTown,
                    postcode: property.propertyPostCode,
                    regionId: property.propertyRegionId,
                    countyId: property.propertyCountyId,
                    boroughId: property.propertyBoroughId,
                    businessEstateId: property.propertyBusinessEstateId,
                    nearestStationId: property.propertyStationId,
                    geolocation:
                        property.propertyLat && property.propertyLong
                            ? {
                                  lat: property.propertyLat,
                                  lng: property.propertyLong,
                              }
                            : null,
                };
            }

            if (section === 'description') {
                initialValues = {
                    availableFrom: property.propertyAvailableFrom,
                    status: property.propertyStatus,
                    propertyTypeIds: property.propertyTypes.map(
                        type => type.propertyTypeId
                    ),
                    description: property.propertyDescription,
                    locationDescription: property.propertyLocationDescription,
                    userPlanning: property.propertyUserPlanning,
                    otherInformation: property.propertyOtherInformation,
                    serviceCharge: property.propertyServiceCharge,
                    maintenance: property.propertyMaintenance,
                };
            }

            if (section === 'key-information') {
                initialValues = {
                    keyDetails1: property.propertyKeyDetails1,
                    keyDetails2: property.propertyKeyDetails2,
                    keyDetails3: property.propertyKeyDetails3,
                    keyDetails4: property.propertyKeyDetails4,
                    keyDetails5: property.propertyKeyDetails5,
                    agreementTypeId: property.propertyPropertyAgreementTypeId,
                    agreementTypeDescription:
                        property.propertyPropertyAgreementDescription,
                    solutionsRPI: property.propertySolutionsRPI,
                    rent: property.propertyRent,
                    rentText: property.propertyRentText,
                    size: property.propertySize,
                    rateableValue: property.propertyRateableValue,
                    insuranceTypeId: property.propertyPropertyInsuranceId,
                    insuranceTypeOther: property.propertyPropertyInsuranceOther,
                    insuranceAmount: property.propertyPropertyInsuranceAmount,
                    repairGuarantee: property.propertyHasRepairGuarantee
                        ? 'yes'
                        : 'no',
                };
            }

            if (section === 'costs') {
                initialValues = {
                    localAuthority: property.propertyLocalAuthority,
                    deposit: property.propertyDeposit,
                    costs: property.propertyCosts,
                    businessRates: property.propertyBusinessRates,
                    promotionType: property.propertyPromoType,
                    promotionAfterPrice: property.propertyPromoAfterPrice,
                    rentFreeMonths: property.propertyPromoRentFreeMonths,
                };
            }

            if (section === 'contacts') {
                initialValues = {
                    contacts: Array.isArray(property.contacts)
                        ? property.contacts
                        : [],
                    showContactMoreInfo: property.propertyContactMoreInfo
                        ? 'yes'
                        : 'no',
                    contactRequestByRegion: property.propertyIsContactRequestByRegion
                        ? 'yes'
                        : 'no',
                    contactRequestRegionId:
                        property.propertyContactRequestRegionId,
                };
            }

            if (section === 'media') {
                initialValues = {
                    brochureAsset: property.propertyBrochureAsset,
                    mainPhotoAsset: property.propertyMainPhotoAsset,
                    photos: Array.isArray(property.imageLookups)
                        ? property.imageLookups.map(lookup => {
                              return {
                                  asset: lookup.image,
                              };
                          })
                        : [],
                    additionalFiles: Array.isArray(property.fileLookups)
                        ? property.fileLookups.map(lookup => {
                              return {
                                  asset: lookup.file,
                              };
                          })
                        : [],
                    videos: Array.isArray(property.videos)
                        ? property.videos.map(video => {
                              return {
                                  videoCode: video.propertyVideoCode,
                                  videoTitle: video.propertyVideoTitle,
                              };
                          })
                        : [],
                    plasmaVideoCode: property.propertyPlasmaVideoCode,
                    plasmaVideoTitle: property.propertyPlasmaVideoTitle,
                };
            }

            if (section === 'activity-log') {
                activityLogList = property.propertyUpdateLogs;
            }

            return (
                <FormWrapper>
                    <PropertyNav
                        propertyReference={propertyReference}
                        sectionStatuses={sectionStatuses}
                    />

                    {section === undefined &&
                        <PropertyLocationForm
                            boroughs={boroughs}
                            businessEstates={businessEstates}
                            counties={counties}
                            initialValues={initialValues}
                            onSubmit={this.onSubmit}
                            regions={regions}
                            stationOptions={stationOptions}
                        />}

                    {section === 'description' &&
                        <PropertyDescriptionForm
                            initialValues={initialValues}
                            onSubmit={this.onSubmit}
                            propertyTypeOptions={propertyTypeOptions}
                            propertyStatusOptions={propertyStatusOptions}
                            standardText={standardText}
                        />}

                    {section === 'key-information' &&
                        <PropertyKeyInformationForm
                            initialValues={initialValues}
                            onSubmit={this.onSubmit}
                            agreementTypeOptions={agreementTypeOptions}
                            insuranceTypeOptions={insuranceTypeOptions}
                            standardText={standardText}
                        />}

                    {section === 'costs' &&
                        <PropertyCostsForm
                            initialValues={initialValues}
                            onSubmit={this.onSubmit}
                            propertyPromotionOptions={propertyPromotionOptions}
                            standardText={standardText}
                        />}

                    {section === 'contacts' &&
                        <PropertyContactForm
                            initialValues={initialValues}
                            onSubmit={this.onSubmit}
                            fetchingContacts={fetchingContacts}
                            contactList={contactList}
                            reassignedContacts={reassignedContacts}
                            contactRequestRegionOptions={
                                contactRequestRegionOptions
                            }
                        />}

                    {section === 'media' &&
                        <PropertyMediaForm
                            initialValues={initialValues}
                            onSubmit={this.onSubmit}
                            propertyReference={propertyReference}
                        />}
                    {section === 'activity-log' &&
                        <PropertyActivityLogForm
                            activityLogList={activityLogList}
                        />}
                </FormWrapper>
            );
        }

        return null;
    }
}

const mapStateToProps = state => {
    const contactList = state.contacts.list;
    const property = state.property;
    const propertyDetail = property.detail;
    const shared = state.shared;
    const defaulttext = state.defaulttext;

    return {
        boroughs: shared.boroughs,
        businessEstates: shared.businessEstates,
        counties: shared.counties,
        errorFetching: propertyDetail.error,
        isFetching: propertyDetail.fetching,
        property: propertyDetail.property,
        regions: shared.regions,
        agreementTypeOptions: getAgreementTypeOptionList(state),
        stationOptions: getStationOptionList(state),
        propertyTypeOptions: getPropertyTypeOptionList(state),
        insuranceTypeOptions: getInsuranceTypeOptionList(state),
        propertyStatusOptions: getPropertyStatusOptionList(state),
        propertyPromotionOptions: getPropertyPromotionTypesOptionList(state),
        fetchingStations: state.stations.list.fetching,
        fetchingContacts: contactList.fetching,
        contactList: getContacts(state),
        standardText: {
            fetching:
                defaulttext.fetching ||
                property.agreementTypes.fetching ||
                property.insuranceTypes.fetching,
            defaultTextItems: getDefaultTextsByFieldName(state),
            agreementTypes: property.agreementTypes.list,
            insuranceTypes: property.insuranceTypes.list,
        },
        contactRequestRegionOptions: state.contactrequests.regions.list,
    };
};

export default withRouter(connect(mapStateToProps)(PropertyEdit));



// WEBPACK FOOTER //
// ./src/containers/PropertyEdit/PropertyEdit.js